import { createAdminV2Error } from '@/admin-v2/foundation/errors';
import type {
  AdminV2ComputedResolverDefinition,
  AdminV2PreflightValidatorDefinition,
} from '../tool-runtime-registries';

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function nonEmptyArray(value: unknown) {
  return Array.isArray(value) && value.length > 0;
}

type SeedanceReferenceKind = 'Image' | 'Video' | 'Audio';

type SeedancePromptTokenFormatter = (
  mediaKind: SeedanceReferenceKind,
  index: string
) => string;

const APIMART_25_REFERENCE_LABELS: Record<SeedanceReferenceKind, string> = {
  Image: '图片',
  Video: '视频',
  Audio: '音频',
};

function compileSeedancePrompt(
  value: unknown,
  formatToken: SeedancePromptTokenFormatter
) {
  const prompt = typeof value === 'string' ? value : '';
  return prompt.replace(
    /\{\{(Image|Video|Audio)(\d+)\}\}/g,
    (_match, mediaKind: SeedanceReferenceKind, index: string) =>
      formatToken(mediaKind, index)
  );
}

const formatApimartReferenceToken: SeedancePromptTokenFormatter = (
  mediaKind,
  index
) => `${mediaKind.toLowerCase()} ${index}`;

const formatApimart25ReferenceToken: SeedancePromptTokenFormatter = (
  mediaKind,
  index
) => `@${APIMART_25_REFERENCE_LABELS[mediaKind]}${index}`;

const formatReplicateReferenceToken: SeedancePromptTokenFormatter = (
  mediaKind,
  index
) => `[${mediaKind}${index}]`;

function assertLastFrameHasFirstFrame(
  normalizedInput: Record<string, unknown>
) {
  if (
    nonEmptyString(normalizedInput.lastFrame) &&
    !nonEmptyString(normalizedInput.firstFrame)
  ) {
    throw createAdminV2Error({
      code: 'SEEDANCE_LAST_FRAME_REQUIRES_FIRST_FRAME',
      message: 'Last frame requires a first frame.',
      fieldPath: 'input.lastFrame',
      status: 422,
    });
  }
}

export const seedanceComputedResolverDefinitions = {
  seedanceApimartPrompt: {
    outputType: 'string',
    resolver: ({ normalizedInput }) =>
      compileSeedancePrompt(
        normalizedInput.prompt,
        formatApimartReferenceToken
      ),
  },
  seedance25ApimartPrompt: {
    outputType: 'string',
    resolver: ({ normalizedInput }) =>
      compileSeedancePrompt(
        normalizedInput.prompt,
        formatApimart25ReferenceToken
      ),
  },
  seedanceReplicatePrompt: {
    outputType: 'string',
    resolver: ({ normalizedInput }) =>
      compileSeedancePrompt(
        normalizedInput.prompt,
        formatReplicateReferenceToken
      ),
  },
  seedanceApimartImageWithRoles: {
    outputType: 'json',
    resolver: ({ normalizedInput }) => {
      const entries: Array<{
        url: string;
        role: 'first_frame' | 'last_frame';
      }> = [];
      if (nonEmptyString(normalizedInput.firstFrame)) {
        entries.push({
          url: normalizedInput.firstFrame,
          role: 'first_frame',
        });
      }
      if (nonEmptyString(normalizedInput.lastFrame)) {
        entries.push({
          url: normalizedInput.lastFrame,
          role: 'last_frame',
        });
      }
      return entries;
    },
  },
} satisfies Record<string, AdminV2ComputedResolverDefinition>;

export const seedancePreflightValidatorDefinitions = {
  seedanceInputCompatibility: {
    validator: ({ normalizedInput }) => {
      assertLastFrameHasFirstFrame(normalizedInput);

      const firstFrame = nonEmptyString(normalizedInput.firstFrame);
      const lastFrame = nonEmptyString(normalizedInput.lastFrame);
      const hasReferenceImages = nonEmptyArray(normalizedInput.referenceImages);
      const hasReferenceVideos = nonEmptyArray(normalizedInput.referenceVideos);
      const hasReferenceAudios = nonEmptyArray(normalizedInput.referenceAudios);

      if ((firstFrame || lastFrame) && hasReferenceImages) {
        throw createAdminV2Error({
          code: 'SEEDANCE_FRAME_REFERENCE_CONFLICT',
          message:
            'First/last frame controls cannot be combined with reference images.',
          fieldPath: 'input.referenceImages',
          status: 422,
        });
      }
      if (
        (firstFrame || lastFrame) &&
        (hasReferenceVideos || hasReferenceAudios)
      ) {
        throw createAdminV2Error({
          code: 'SEEDANCE_FRAME_MULTIMODAL_REFERENCE_CONFLICT',
          message:
            'First/last frame controls cannot be combined with video or audio references.',
          fieldPath: 'input.firstFrame',
          status: 422,
        });
      }
      if (hasReferenceAudios && !hasReferenceImages && !hasReferenceVideos) {
        throw createAdminV2Error({
          code: 'SEEDANCE_AUDIO_REFERENCE_REQUIRES_VISUAL_REFERENCE',
          message:
            'Reference audio requires at least one reference image or video.',
          fieldPath: 'input.referenceAudios',
          status: 422,
        });
      }
    },
  },
  seedance25InputCompatibility: {
    validator: ({ normalizedInput }) => {
      assertLastFrameHasFirstFrame(normalizedInput);

      const hasFrame =
        nonEmptyString(normalizedInput.firstFrame) ||
        nonEmptyString(normalizedInput.lastFrame);
      const hasReferenceImages = nonEmptyArray(normalizedInput.referenceImages);
      const hasTimedReference =
        nonEmptyArray(normalizedInput.referenceVideos) ||
        nonEmptyArray(normalizedInput.referenceAudios);

      if (hasFrame && hasReferenceImages) {
        throw createAdminV2Error({
          code: 'SEEDANCE_25_FRAME_REFERENCE_CONFLICT',
          message:
            'First/last frame controls cannot be combined with reference images.',
          fieldPath: 'input.referenceImages',
          status: 422,
        });
      }
      if (hasFrame && hasTimedReference) {
        throw createAdminV2Error({
          code: 'SEEDANCE_25_FRAME_MULTIMODAL_REFERENCE_CONFLICT',
          message:
            'First/last frame controls cannot be combined with video or audio references.',
          fieldPath: 'input.firstFrame',
          status: 422,
        });
      }
      if (hasFrame && normalizedInput.aspect_ratio !== 'adaptive') {
        throw createAdminV2Error({
          code: 'SEEDANCE_25_FRAME_REQUIRES_ADAPTIVE_ASPECT_RATIO',
          message:
            'Seedance 2.5 frame-controlled generation requires Adaptive.',
          fieldPath: 'input.aspect_ratio',
          status: 422,
        });
      }
    },
  },
} satisfies Record<string, AdminV2PreflightValidatorDefinition>;
